import { useState, useEffect, useRef, useCallback } from "react";

interface Props {
  durationSeconds: number;
  callId: number;
  currentlyPlaying: number | null;
  onPlay: (callId: number) => void;
  onPause: () => void;
}

function formatTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.floor(totalSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function MiniWaveformPlayer({ durationSeconds, callId, currentlyPlaying, onPlay, onPause }: Props) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const isActive = playing && currentlyPlaying === callId;

  useEffect(() => {
    if (currentlyPlaying !== callId && playing) {
      setPlaying(false);
      setProgress(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [currentlyPlaying, callId, playing]);

  useEffect(() => {
    if (isActive) {
      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          const next = prev + 1;
          if (next >= durationSeconds) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setPlaying(false);
            onPause();
            return durationSeconds;
          }
          return next;
        });
      }, 100);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, durationSeconds, onPause]);

  const togglePlay = useCallback(() => {
    if (playing && currentlyPlaying === callId) {
      setPlaying(false);
      onPause();
    } else {
      setPlaying(true);
      onPlay(callId);
    }
  }, [playing, currentlyPlaying, callId, onPlay, onPause]);

  const handleBarClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!barRef.current) return;
      const rect = barRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setProgress(Math.floor(ratio * durationSeconds));
    },
    [durationSeconds]
  );

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e: MouseEvent) => {
      if (!barRef.current) return;
      const rect = barRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setProgress(Math.floor(ratio * durationSeconds));
    };
    const handleUp = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isDragging, durationSeconds]);

  const pct = (progress / durationSeconds) * 100;

  return (
    <div className="inline-flex items-center gap-2 bg-green-50 rounded-lg px-3 py-1.5">
      {/* Play/Pause button */}
      <button
        onClick={togglePlay}
        className="w-6 h-6 flex items-center justify-center rounded-full bg-green-600 hover:bg-green-700 text-white transition-colors flex-shrink-0"
      >
        <i className={`text-[10px] ${isActive ? "ri-pause-fill" : "ri-play-fill"}`} />
      </button>

      {/* Progress bar */}
      <div
        ref={barRef}
        onClick={handleBarClick}
        className="relative flex-1 min-w-[60px] h-1.5 bg-gray-200 rounded-full cursor-pointer"
      >
        <div
          className="absolute inset-y-0 left-0 bg-green-500 rounded-full transition-all duration-75"
          style={{ width: `${pct}%` }}
        />
        <div
          onMouseDown={handleDragStart}
          className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full border border-green-500 shadow-sm cursor-grab active:cursor-grabbing"
          style={{ left: `calc(${pct}% - 4px)` }}
        />
      </div>

      {/* Time */}
      <span className="text-xs text-[#6b7280] whitespace-nowrap flex-shrink-0">
        {formatTime(progress)} / {formatTime(durationSeconds)}
      </span>
    </div>
  );
}